/*
 * The 4-step Launch wizard (U7) — full-screen page shell occupying the app
 * Shell's main content area. Steps: Product · Tickets · Settings · Preview.
 * Preview is the TERMINAL step: the "Launch" confirm action fires from it (the
 * redundant 5th "Launch" step that re-rendered Preview has been removed). Step
 * state survives Back-navigation.
 *
 * The chrome is now Mantine's `Stepper` (`active={step-1}`,
 * `allowNextStepsSelect={false}` so only completed steps are reachable by
 * back-nav). Each step's content is the matching `Stepper.Step`'s children;
 * Stepper renders only the active step's content.
 *
 * Preview-run lifecycle (unchanged):
 *   - Entering Preview (step 4) fires POST /runs with `confirm_before_launch:true`
 *     once per (selection + settings) attempt, deduplicated by the stable
 *     idempotency key. The key regenerates on the step-3→4 transition so each
 *     fresh attempt creates a new run.
 *   - Step4Preview renders the live prep progress (WS) then the confirmation table.
 *   - Back / Cancel / Exit from step 4 aborts the preview run optimistically (fire
 *     and forget) and clears the stored run id so re-entering creates a fresh run.
 *   - The Preview "Launch" button calls POST /runs/{id}/confirm then navigates to
 *     the run detail. The button is disabled while any mutation is pending.
 *   - Step2SelectTickets receives the refresh props; after a Refresh-selected the
 *     wizard drops any returned rows that are now already_queued from the selection.
 *   - Step3LaunchDefaults receives productKey so the Admanage queries can fire; on
 *     Preview it renders disabled as a read-only summary atop the table.
 *
 * Because the wizard is Shell content (left nav rail is visible), two extra
 * affordances guard against accidental navigation:
 *
 *   1. An "Exit wizard" button in the header (in addition to Cancel in the
 *      footer) gives the operator an explicit labeled exit.
 *   2. A WizardNavBlocker sub-component mounts from step 2 onward and installs
 *      a React Router useBlocker so a nav-rail click mid-wizard prompts before
 *      discarding state. From step 4 the blocker message states that leaving
 *      will cancel the in-flight preview run. The sub-component is only rendered
 *      when a data-router context is detected (via UNSAFE_DataRouterContext check
 *      in the parent) so MemoryRouter-based test harnesses work without error.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, UNSAFE_DataRouterContext } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Fieldset,
  Group,
  SegmentedControl,
  Stepper,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { errorMessage } from "../../api/errors";
import {
  useCreateRun,
  useConfirmRun,
  useLaunchTickets,
  useLastRunDefaults,
  useRefreshSelectedTickets,
  useRunPreview,
  useRun,
} from "../../api/queries";
import { cancelRun } from "../../api/endpoints";
import type { RunDetail, RunConfirm } from "../../api/client";
import { useRunStatus } from "../../ws/useRunStatus";
import { Step1SelectProduct } from "./Step1SelectProduct";
import { Step2SelectTickets } from "./Step2SelectTickets";
import {
  Step3LaunchDefaults,
  defaultStartTimeLocal,
  isValidPageId,
  isValidLink,
  isValidBid,
  isValidStartTime,
  localToIsoOffset,
  type LaunchDefaults,
} from "./Step3LaunchDefaults";
import { Step4Preview } from "./Step4Preview";
import { launchableAdIds, toggleOverride } from "./namingOverride";
import { GroupPreviewSection } from "./GroupPreviewSection";
import { GroupTargetSummary } from "./GroupTargetSummary";

type StepIndex = 1 | 2 | 3 | 4;

/**
 * One POV group of the Step-2 selection (POV→page fan-out, 2026-07-06). The
 * key is the ticket's exact `Ad Page/POV` combination (comma-joined, Notion
 * order) — tickets with NO POV form their own `No POV` group; nothing is
 * excluded or guessed. When the selection spans >1 group the wizard collects
 * Settings PER GROUP and creates one gated run per group.
 */
interface PovGroup {
  key: string;
  label: string;
  pageIds: string[];
}

const NO_POV_KEY = "No POV";

/** Ordered step labels (Preview is the terminal step). */
const STEP_LABELS: Record<StepIndex, string> = {
  1: "Product",
  2: "Tickets",
  3: "Settings",
  4: "Preview",
};

/**
 * Fresh Step-3 defaults. A FUNCTION (not a const) because `start_time`
 * prefills to ~1.5 hours ahead (#8 — every wizard launch is scheduled;
 * 2026-07-08: was 2h, but the cap bypass proved unreliable at that lead).
 */
function freshDefaults(): LaunchDefaults {
  return {
    page_id: "",
    link: "",
    target_campaign_id: "",
    source_adset_id: "",
    name_swap: "",
    start_time: defaultStartTimeLocal(),
    bid_mode: "product",
    bid_amount: "",
    opt_goal: "product",
    // 2026-07-31: combine mode (retargeting) — off by default.
    combine_adsets: false,
    combine_adset_name: "",
    // 2026-10-20: duplicate by default; launching into an existing ad set is opt-in.
    into_existing_adset: false,
  };
}

/**
 * The per-launch bid override for the RunCreate body (2026-07-17).
 * "product" ⇒ no key (the product config applies); "none" ⇒ bid_amount:null
 * (launch WITHOUT bid fields); "custom" ⇒ the entered amount.
 */
/**
 * The run body's go-live time and ad set option (2026-10-20). Launching into
 * an existing ad set creates nothing, so its go-live field is hidden: a fresh
 * default still satisfies the scheduled path, which ignores it for that mode.
 */
function adsetModeBody(d: LaunchDefaults): {
  start_time: string;
  into_existing_adset?: boolean;
  bid_amount?: number | null;
  optimization_goal?: string;
} {
  if (d.into_existing_adset) {
    return { start_time: localToIsoOffset(defaultStartTimeLocal()), into_existing_adset: true };
  }
  return { start_time: localToIsoOffset(d.start_time), ...bidOverrideBody(d) };
}

function bidOverrideBody(
  d: LaunchDefaults,
): { bid_amount?: number | null; optimization_goal?: string } {
  const goal = d.opt_goal !== "product" ? { optimization_goal: d.opt_goal } : {};
  if (d.bid_mode === "none") return { bid_amount: null, ...goal };
  if (d.bid_mode === "custom") return { bid_amount: Number(d.bid_amount.trim()), ...goal };
  return { ...goal };
}

/**
 * Run statuses at which the preview poll stops — the run will not advance
 * further. `drafted` is a terminal SUCCESS (a Draft confirm rests here); it MUST
 * be present or the poll would hang after a Draft (the load-bearing stop). The
 * backend emits `aborted` (never `cancelled`), so the old `cancelled` alias is
 * dropped.
 */
const PREVIEW_TERMINAL = new Set([
  "failed",
  "aborted",
  "completed",
  "drafted",
]);

/**
 * Installs a React Router navigation blocker while mounted. Placed in a
 * dedicated sub-component so that `useBlocker` (which requires a data-router
 * context) is only called when the parent has confirmed that context exists —
 * rendering this component inside a bare `MemoryRouter` would still throw, so
 * the parent must guard with `useContext(UNSAFE_DataRouterContext) !== null`
 * before rendering it.
 *
 * From step 4 onward (`atPreviewStep`) the confirmation message warns that
 * leaving will cancel the in-flight preview run.
 */
function WizardNavBlocker({
  atPreviewStep,
  onCancelPreview,
  bypassRef,
}: {
  atPreviewStep: boolean;
  onCancelPreview: () => void;
  /** When `.current` is true, allow the navigation without a prompt (U8 — a
   *  successful submit navigates programmatically and must not be challenged). */
  bypassRef: { current: boolean };
}) {
  const blocker = useBlocker(useCallback(() => !bypassRef.current, [bypassRef]));

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    const msg = atPreviewStep
      ? "Leaving now will cancel the in-flight preview run and discard the staged creatives. Continue?"
      : "You have unsaved wizard state. Leave the Launch wizard?";

    if (window.confirm(msg)) {
      if (atPreviewStep) {
        onCancelPreview();
      }
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, atPreviewStep, onCancelPreview]);

  return null;
}

export function LaunchWizard({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const [step, setStep] = useState<StepIndex>(1);
  const [productKey, setProductKey] = useState("");
  const [flow, setFlow] = useState("");
  const [format, setFormat] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // 2026-07-01-002: per-ticket chosen copy sub-tab (pageId → tabId) for tickets
  // whose FinalCopyFile links an empty parent tab. Lifted here so it survives a
  // Step-2 ticket refresh (the grid re-renders with the same picks).
  const [copyTabPicks, setCopyTabPicks] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<LaunchDefaults>(freshDefaults);
  const [step3Submitted, setStep3Submitted] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // ---- POV→page fan-out state (multi-group path only) ---------------------

  /** Per-group Settings, keyed by the group's POV combination. */
  const [groupDefaults, setGroupDefaults] = useState<Record<string, LaunchDefaults>>({});
  /** Per-group preview-run state (runId once created, or the create error). */
  const [groupRuns, setGroupRuns] = useState<
    Record<string, { runId: string | null; error: unknown | null }>
  >({});
  /** Groups whose gated run has been confirmed (per-group Launch clicked). */
  const [launchedGroups, setLaunchedGroups] = useState<Set<string>>(new Set());
  /** One idempotency key per group per Preview attempt (regenerated on 3→4). */
  const groupIdemKeys = useRef<Record<string, string>>({});
  /** Live mirror of groupRuns for the sequential create loop / retry path. */
  const groupRunsRef = useRef(groupRuns);
  useEffect(() => {
    groupRunsRef.current = groupRuns;
  }, [groupRuns]);

  // Per-ad selection (U12) — empty ⇒ launch ALL ready ads (the inversion is
  // surfaced explicitly in Step4Preview). Reset on each fresh Preview attempt.
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set());
  // 2026-10-06: tickets with blank naming cells the operator launches anyway.
  // Reset with the selection on each fresh Preview attempt.
  const [namingOverrides, setNamingOverrides] = useState<Set<string>>(new Set());
  // Launch mode (U9): Published (default) pushes to Meta; Draft stages the
  // creatives in AdManage only (no Meta push). The per-ad select column is enabled
  // in BOTH modes now (U7).
  const [launchMode, setLaunchMode] = useState<"PUBLISHED" | "DRAFT">("PUBLISHED");
  // U8: set true synchronously right before the post-launch navigations so the
  // WizardNavBlocker doesn't pop a false "leaving will cancel the run" prompt on a
  // SUCCESSFUL submit. A ref (read live by the blocker function at navigation time)
  // — NOT state — because setState wouldn't update the blocker before the synchronous
  // navigate() calls fire.
  const navigatedAfterLaunch = useRef(false);

  // ---- Preview-run state -------------------------------------------------

  /**
   * The preview-run id created when entering step 4. Null until the run
   * creation completes. Cleared when Back/Cancel cancels the run.
   */
  const [previewRunId, setPreviewRunId] = useState<string | null>(null);

  /**
   * Error from the POST /runs call. Shown inline in Step4Preview with
   * Retry / Back-to-Settings affordances.
   */
  const [previewCreateError, setPreviewCreateError] = useState<unknown | null>(null);

  /**
   * Whether the create-run mutation is currently in flight (used to gate the
   * "Launch" button and block re-entry).
   */
  const [isCreatingRun, setIsCreatingRun] = useState(false);

  // One idempotency key per wizard instance — a double-submit returns the
  // original run. The key is regenerated on the step-3 → step-4 transition
  // so each fresh Preview attempt creates a new run.
  const idempotencyKey = useRef(crypto.randomUUID());

  // The tickets query is owned here so the resolved product_id + selection
  // survive Back-navigation; it only fetches once Step 2 is reached.
  const ticketsQuery = useLaunchTickets(productKey, flow, format, step >= 2);

  // Refresh-selected mutation: merges fresh rows into the TanStack cache.
  const refreshSelected = useRefreshSelectedTickets(productKey, flow, format);

  /**
   * The Step-2 selection grouped by exact `Ad Page/POV` combination. One group
   * (the overwhelmingly common case) keeps the wizard on the original
   * single-run path — byte-identical behavior; >1 group activates the fan-out
   * path (per-group Settings, one gated run per group).
   */
  const groups: PovGroup[] = useMemo(() => {
    const rows = ticketsQuery.data?.tickets ?? [];
    const byId = new Map(rows.map((r) => [r.page_id, r]));
    const buckets = new Map<string, string[]>();
    for (const id of selected) {
      const row = byId.get(id);
      const key =
        row && row.ad_page_pov.length > 0 ? row.ad_page_pov.join(", ") : NO_POV_KEY;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(id);
      else buckets.set(key, [id]);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, pageIds]) => ({
        key,
        pageIds,
        label: `${key} — ${pageIds.length} ticket${pageIds.length !== 1 ? "s" : ""}`,
      }));
  }, [ticketsQuery.data, selected]);

  const multiGroup = groups.length > 1;

  // Guard for the WizardNavBlocker: only render it when a data-router
  // context is present. useBlocker itself throws an invariant inside a plain
  // MemoryRouter (no DataRouterContext), so we check here before mounting.
  const hasDataRouter = useContext(UNSAFE_DataRouterContext) !== null;

  // ---- Live WS stream for the preview run ---------------------------------

  const atPreview = step === 4;

  const runStatus = useRunStatus(previewRunId, {
    enabled: previewRunId !== null && atPreview,
  });

  const wsStatus = runStatus.latest?.status?.toLowerCase() ?? null;

  // Enable the preview read once AWAITING_CONFIRM is reached via WS.
  const wsReady = previewRunId !== null && wsStatus === "awaiting_confirm";

  /**
   * WS-INDEPENDENT status poll. The live WS (`useRunStatus`) is the fast path,
   * but the browser WS can fail to connect (the SPA's subprotocol-auth quirk) or
   * the run can fail before the SPA ever subscribes — in which case nothing would
   * ever update and the Preview would hang on "Starting…" with no network
   * activity. So, after a short grace (letting the WS happy-path deliver its frame
   * first), we poll `GET /runs/{id}` every 2.5s REGARDLESS of WS state until the
   * run reaches the gate (AWAITING_CONFIRM) or a terminal status (FAILED / ABORTED
   * / CANCELLED / COMPLETED). The polled status drives `effectiveRunStatus`, so a
   * failure (or progress, or the gate) is surfaced even when the WS is dead.
   */
  const [pollStarted, setPollStarted] = useState(false);
  useEffect(() => {
    setPollStarted(false);
    if (previewRunId === null || !atPreview) return;
    const t = setTimeout(() => setPollStarted(true), 1500);
    return () => clearTimeout(t);
  }, [previewRunId, atPreview]);

  const runPoll = useRun(previewRunId ?? "", {
    enabled: previewRunId !== null && atPreview && pollStarted && !wsReady,
    retry: false,
    refetchInterval: (query) => {
      const st = (query.state.data as RunDetail | undefined)?.status?.toLowerCase();
      if (st === "awaiting_confirm" || (st !== undefined && PREVIEW_TERMINAL.has(st))) {
        return false; // stop polling once the run is at the gate or terminal
      }
      return 2500;
    },
  });

  const polledStatus = runPoll.data?.status?.toLowerCase() ?? null;
  const polledReady = polledStatus === "awaiting_confirm";

  // The preview is "ready" once the run reaches the AWAITING_CONFIRM gate (via the
  // live WS frame or the poll).
  const previewReady = wsReady || polledReady;

  // The frame Step4Preview renders: the live WS frame if present, else one
  // synthesized from the polled run (status + current_stage) so the gate, live
  // progress, AND a terminal failure all surface even when the WS never connected.
  const effectiveRunStatus =
    runStatus.latest ??
    (runPoll.data && previewRunId
      ? {
          run_id: previewRunId,
          stage: runPoll.data.current_stage ?? "prep",
          status: runPoll.data.status,
        }
      : null);

  // The prepped per-ad table data — fetched once the run is ready (WS or poll).
  const previewQuery = useRunPreview(previewRunId ?? "", previewReady, {
    retry: false,
  });

  // Confirm mutation (Preview Launch). The mode + selection wiring is U9; for
  // U7 the existing no-body confirm call path is preserved.
  const confirmRun = useConfirmRun(previewRunId ?? "");

  // ---- Cancel a preview run optimistically --------------------------------

  const cancelPreviewRun = useCallback(() => {
    // POV fan-out: cancel every group run that was created but NOT yet
    // launched (a confirmed run is live work — never cancel it here).
    const groupIds = Object.entries(groupRuns)
      .filter(([key, v]) => v.runId !== null && !launchedGroups.has(key))
      .map(([, v]) => v.runId as string);
    if (groupIds.length > 0) {
      setGroupRuns({});
      for (const id of groupIds) {
        void cancelRun(id).catch(() => {
          // Best-effort — the gated-run sweep reaps abandoned AWAITING_CONFIRM runs.
        });
      }
    }
    if (!previewRunId) return;
    const runId = previewRunId;
    // Optimistic: clear local state immediately; don't await the cancel.
    // Clearing previewRunId disables the status poll + resets pollStarted.
    setPreviewRunId(null);
    setPreviewCreateError(null);
    void cancelRun(runId).catch(() => {
      // Best-effort; the maintenance sweep reaps abandoned AWAITING_CONFIRM
      // runs. Silently swallow any cancel error.
    });
  }, [previewRunId, groupRuns, launchedGroups]);

  /**
   * A group's per-section Launch confirmed its run. When the LAST group
   * launches, leave the wizard for the Queue (multiple runs — the run-detail
   * navigation of the single-group path doesn't fit N runs).
   */
  function handleGroupLaunched(groupKey: string) {
    setLaunchedGroups((prev) => {
      const next = new Set(prev);
      next.add(groupKey);
      if (next.size === groups.length) {
        navigatedAfterLaunch.current = true;
        notifications.show({
          title: "All groups launched",
          message: `${groups.length} runs created — one per POV group.`,
          color: "green",
        });
        onClose();
        navigate("/");
      }
      return next;
    });
  }

  // ---- Product / flow / selection handlers --------------------------------

  function changeProduct(key: string) {
    if (key === productKey) return;
    setProductKey(key);
    setSelected(new Set());
  }

  function changeFlow(next: string) {
    setFlow(next);
    setSelected(new Set());
  }

  function changeFormat(next: string) {
    setFormat(next);
    setSelected(new Set());
  }

  function toggle(pageId: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(pageId);
      else next.delete(pageId);
      return next;
    });
  }

  function toggleAll(validPageIds: string[], checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of validPageIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  // Per-ad selection toggles (U12). `toggleAd` flips one ad; `toggleAllAds`
  // selects/clears every selectable (ready) ad id.
  function toggleAd(id: string, checked: boolean) {
    setSelectedAdIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /**
   * After a Refresh-selected: drop from the selection any returned rows that
   * are now already_queued (concurrent-launch race — the grid surfaces the
   * inline warning; we own the selection drop here).
   */
  function handleRefreshSelected(pageIds: string[]) {
    refreshSelected.mutate(pageIds, {
      onSuccess: (rows) => {
        const nowQueued = new Set(
          rows.filter((r) => r.already_queued).map((r) => r.page_id),
        );
        if (nowQueued.size > 0) {
          setSelected((prev) => {
            const next = new Set(prev);
            for (const id of nowQueued) next.delete(id);
            return next;
          });
        }
      },
    });
  }

  const selectedCount = selected.size;

  /** One group's Settings are complete (same rule as the single-group path). */
  const groupSettingsValid = (d: LaunchDefaults) =>
    isValidPageId(d.page_id) &&
    isValidLink(d.link) &&
    d.target_campaign_id.trim() !== "" &&
    d.source_adset_id.trim() !== "" &&
    // An existing ad set (2026-10-20) keeps its own schedule and bid, so
    // those fields are hidden and not checked.
    (d.into_existing_adset ||
      // #8: every wizard launch is scheduled — the go-live time must be future.
      (isValidStartTime(d.start_time) &&
        // 2026-07-17: a custom bid needs a positive amount.
        isValidBid(d) &&
        // 2026-07-31: combine mode needs the shared ad set's name.
        (!d.combine_adsets || d.combine_adset_name.trim() !== "")));

  const defaultsFor = (key: string): LaunchDefaults =>
    groupDefaults[key] ?? freshDefaults();

  // ---- Settings prefill from the last run (2026-07-07 #4) ------------------
  //
  // Entering Step 3, EMPTY fields are seeded from the product's most recent
  // non-failed run: single-group gets all four (page / URL / campaign / adset);
  // POV fan-out groups get URL / campaign / adset only (each group launches on
  // its own page — that pick stays manual). Applied AT MOST ONCE per group key
  // per wizard instance, so operator input is never overwritten and a
  // deliberately cleared field is not re-filled on a later Step-3 visit.
  const lastRunDefaults = useLastRunDefaults(productKey, step === 3);
  const prefilledKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const last = lastRunDefaults.data;
    if (step !== 3 || !last || !last.found) return;
    const fill = (d: LaunchDefaults, withPage: boolean): LaunchDefaults => {
      // Campaign + adset seed ONLY as a pair: the last run's adset belongs to
      // the last run's campaign, so seeding the adset under an operator-picked
      // campaign (or vice versa) would silently pair a foreign template adset
      // with the wrong campaign. If the operator touched either field before
      // the query resolved, both are left alone.
      const fillCampaignPair =
        !d.target_campaign_id.trim() &&
        !d.source_adset_id.trim() &&
        Boolean(last.target_campaign_id);
      return {
        ...d,
        ...(withPage && !d.page_id.trim() && last.page_id ? { page_id: last.page_id } : {}),
        ...(!d.link.trim() && last.link ? { link: last.link } : {}),
        ...(fillCampaignPair ? { target_campaign_id: last.target_campaign_id! } : {}),
        ...(fillCampaignPair && last.source_adset_id
          ? { source_adset_id: last.source_adset_id }
          : {}),
      };
    };
    if (multiGroup) {
      for (const g of groups) {
        if (prefilledKeys.current.has(g.key)) continue;
        prefilledKeys.current.add(g.key);
        setGroupDefaults((prev) => ({
          ...prev,
          [g.key]: fill(prev[g.key] ?? freshDefaults(), false),
        }));
      }
    } else if (!prefilledKeys.current.has("__single__")) {
      prefilledKeys.current.add("__single__");
      setDefaults((d) => fill(d, true));
    }
  }, [step, lastRunDefaults.data, multiGroup, groups]);

  // 2026-10-20: a draft makes its own paused ad set, so it cannot add to an
  // existing one. Draft is offered only when no group launches into one.
  const anyIntoExisting = multiGroup
    ? groups.some((g) => defaultsFor(g.key).into_existing_adset)
    : defaults.into_existing_adset;
  useEffect(() => {
    if (anyIntoExisting && launchMode === "DRAFT") setLaunchMode("PUBLISHED");
  }, [anyIntoExisting, launchMode]);
  const launchModeOptions = [
    { label: "Published", value: "PUBLISHED" },
    { label: "Draft", value: "DRAFT", disabled: anyIntoExisting },
  ];

  const step3Valid = multiGroup
    ? groups.every((g) => groupSettingsValid(defaultsFor(g.key)))
    : groupSettingsValid(defaults);

  const canAdvanceFromStep1 = productKey.length > 0;
  const canAdvanceFromStep2 = selectedCount > 0;

  // ---- Navigation ---------------------------------------------------------

  const createRun = useCreateRun();

  /** Fire the preview-run creation (step 3 → 4). One run per idempotency key. */
  function createPreviewRun() {
    const data = ticketsQuery.data;
    if (!data) return;
    if (previewRunId !== null) return; // already created for this key
    if (isCreatingRun) return;

    setPreviewCreateError(null);
    setIsCreatingRun(true);

    createRun.mutate(
      {
        product_id: data.product_id,
        selected_page_ids: [...selected],
        page_id: defaults.page_id.trim(),
        link: defaults.link.trim(),
        target_campaign_id: defaults.target_campaign_id.trim(),
        source_adset_id: defaults.source_adset_id.trim(),
        idempotency_key: idempotencyKey.current,
        confirm_before_launch: true,
        // 2026-07-01-002: send the operator's copy sub-tab picks for the selected
        // tickets only; prep reads the picked tab instead of the empty parent tab.
        copy_tab_overrides: [...selected]
          .filter((pid) => copyTabPicks[pid])
          .map((pid) => ({ page_id: pid, tab_id: copyTabPicks[pid] })),
        // 2026-07-06 (#5): optional persona name for the [NAME] token swap.
        ...(defaults.name_swap.trim() ? { name_swap: defaults.name_swap.trim() } : {}),
        // 2026-07-06 (#8): every wizard launch is SCHEDULED — the adsets land in
        // Meta's "Scheduled" state at this instant (ISO-8601 with local offset).
        // 2026-07-17: per-launch bid choice (absent = product config).
        // 2026-10-20: or the ads go into the chosen existing ad set.
        ...adsetModeBody(defaults),
        // 2026-07-31: combine mode — every ticket into ONE named ad set.
        ...(!defaults.into_existing_adset && defaults.combine_adsets && defaults.combine_adset_name.trim()
          ? { combine_adset_name: defaults.combine_adset_name.trim() }
          : {}),
      },
      {
        onSuccess: (run) => {
          setPreviewRunId(run.id);
          setIsCreatingRun(false);
        },
        onError: (err) => {
          setPreviewCreateError(err);
          setIsCreatingRun(false);
        },
      },
    );
  }

  // ---- Multi-group run creation (POV fan-out) ------------------------------

  /** Create ONE group's gated run (used by the sequential loop and by Retry). */
  async function createGroupRun(g: PovGroup): Promise<void> {
    const data = ticketsQuery.data;
    if (!data) return;
    if (groupRunsRef.current[g.key]?.runId) return; // already created this attempt
    const d = defaultsFor(g.key);
    try {
      const run = await createRun.mutateAsync({
        product_id: data.product_id,
        selected_page_ids: g.pageIds,
        page_id: d.page_id.trim(),
        link: d.link.trim(),
        target_campaign_id: d.target_campaign_id.trim(),
        source_adset_id: d.source_adset_id.trim(),
        idempotency_key: groupIdemKeys.current[g.key],
        confirm_before_launch: true,
        copy_tab_overrides: g.pageIds
          .filter((pid) => copyTabPicks[pid])
          .map((pid) => ({ page_id: pid, tab_id: copyTabPicks[pid] })),
        // 2026-07-06 (#5): per-group persona name (each group ≈ one persona).
        ...(d.name_swap.trim() ? { name_swap: d.name_swap.trim() } : {}),
        // 2026-07-06 (#8): per-group scheduled go-live (usually the shared +4h default),
        // per-group bid choice, or the group's existing ad set (2026-10-20).
        ...adsetModeBody(d),
      });
      setGroupRuns((prev) => ({ ...prev, [g.key]: { runId: run.id, error: null } }));
    } catch (err) {
      setGroupRuns((prev) => ({ ...prev, [g.key]: { runId: null, error: err } }));
    }
  }

  /**
   * Create every group's run SEQUENTIALLY (one AdManage duplicate at a time —
   * the same one-in-flight discipline finalize itself keeps). A failed group
   * records its error and does not block the remaining groups; each section
   * offers its own Retry. Server-side idempotency (per-group keys) dedupes a
   * StrictMode double-fire exactly like the single-run path.
   */
  async function createGroupRuns(): Promise<void> {
    for (const g of groups) {
      // eslint-disable-next-line no-await-in-loop
      await createGroupRun(g);
    }
  }

  function next() {
    if (step === 1 && canAdvanceFromStep1) {
      setStep(2);
    } else if (step === 2 && canAdvanceFromStep2) {
      setStep(3);
    } else if (step === 3) {
      setStep3Submitted(true);
      if (step3Valid) {
        // Regenerate the idempotency key on the step-3 → step-4 transition
        // so each fresh Preview attempt creates a distinct run.
        idempotencyKey.current = crypto.randomUUID();
        // POV fan-out: one fresh key per group per attempt.
        groupIdemKeys.current = Object.fromEntries(
          groups.map((g) => [g.key, crypto.randomUUID()]),
        );
        // Reset any previous preview-run state (clearing previewRunId also
        // disables the status poll + resets pollStarted via its effect).
        setPreviewRunId(null);
        setPreviewCreateError(null);
        setIsCreatingRun(false);
        setGroupRuns({});
        setLaunchedGroups(new Set());
        // Fresh attempt: clear the per-ad selection (the ids belong to the prior
        // run) and default the mode back to Published (U12/U9).
        setSelectedAdIds(new Set());
        setNamingOverrides(new Set());
        setLaunchMode("PUBLISHED");
        setStep(4);
      }
    }
    // Step 4 (Preview) is terminal — there is no "next"; the primary action
    // becomes "Launch" (see the footer below).
  }

  // ---- Refresh-all: prune newly-already_queued tickets --------------------
  //
  // After a Refresh-all refetch resolves, drop from `selected` any page_id
  // that is now `already_queued` in the refreshed data. This mirrors the
  // Refresh-selected pruning in `handleRefreshSelected` but is applied over
  // the full `ticketsQuery.data.tickets` result whenever it changes.
  useEffect(() => {
    const tickets = ticketsQuery.data?.tickets;
    if (!tickets) return;
    const nowQueued = new Set(
      tickets.filter((t) => t.already_queued).map((t) => t.page_id),
    );
    if (nowQueued.size === 0) return;
    setSelected((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of nowQueued) {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [ticketsQuery.data]);

  // When we enter step 4, fire the run creation (once per attempt).
  // `previewRunId` and `isCreatingRun` are reset to null/false in `next()` on
  // the step-3→4 transition, so the effect fires a fresh mutation on each entry.
  // The idempotency key (a ref) deduplicates accidental double-fires from React
  // StrictMode double-mount via the server-side advisory lock.
  useEffect(() => {
    if (step === 4) {
      if (multiGroup) {
        void createGroupRuns();
      } else {
        createPreviewRun();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Scroll the page to the top on every step transition. The wizard is a tall
  // full-bleed page; advancing (especially to the terminal Preview step, where the
  // live PREP/UPLOAD status streams at the TOP) otherwise leaves the viewport
  // stranded at the footer where "Next"/"Launch" was — so the operator sees no
  // feedback and can't tell the run is progressing. (Same scroll-to-top affordance
  // ProductsPage uses after a save.)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // A launch/confirm error renders in an Alert at the TOP of the step; if the
  // operator is down by the footer's Launch button they'd never see it. Bring it
  // into view.
  useEffect(() => {
    if (launchError) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [launchError]);

  function back() {
    setLaunchError(null);
    if (step === 4) {
      // Cancel the preview run optimistically before going back to Settings.
      cancelPreviewRun();
      setStep(3);
    } else if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    }
  }

  function exit() {
    if (step === 4) {
      cancelPreviewRun();
    }
    onClose();
  }

  /**
   * Launch = confirm the gated preview run with the Preview-stage decision
   * (mode + selection), then navigate to the run detail. Draft shows a
   * notification and surfaces a Draft-specific error message (U9).
   */
  function launch() {
    if (!previewRunId) return;
    setLaunchError(null);
    const isDraft = launchMode === "DRAFT";
    const body: RunConfirm = { mode: launchMode };
    // Send the per-ad selection for BOTH modes when a subset is chosen (U7). For
    // Draft the selection is seeded to all on entry, so a non-empty set means the
    // operator's explicit choice; an empty set is blocked at submit (U8/KTD-10),
    // never sent. For Published, empty ⇒ omit (backend's empty⇒all default).
    if (selectedAdIds.size > 0) {
      body.selected_ad_ids = [...selectedAdIds];
    }
    if (namingOverrides.size > 0) {
      body.naming_override_ticket_ids = [...namingOverrides];
    }
    confirmRun.mutate(body, {
      onSuccess: () => {
        // U8: suppress the nav blocker BEFORE navigating — both onClose() (→ "/")
        // and the navigate below are intercepted otherwise, popping a false prompt.
        navigatedAfterLaunch.current = true;
        if (isDraft) {
          notifications.show({
            title: "Saved as draft",
            message: "Creatives staged in AdManage — not pushed to Meta.",
            color: "blue",
          });
        }
        onClose();
        navigate(`/runs/${previewRunId}`);
      },
      onError: (err) =>
        setLaunchError(
          isDraft
            ? `Could not save draft: ${errorMessage(err)}`
            : errorMessage(err),
        ),
    });
  }

  // Derived: can the Launch button be clicked?
  const previewTickets = previewQuery.data?.tickets ?? [];
  const readyTicketCount = previewTickets.filter((t) => t.ok).length;
  const allTicketsFailed =
    previewTickets.length > 0 && readyTicketCount === 0;
  const pendingAnyMutation = isCreatingRun || createRun.isPending || confirmRun.isPending;

  // Per-ad selection derived state (U12). Selectable = ads on ready (ok) tickets.
  // Held ads (blank naming cells, not overridden) are not selectable and are left
  // out of every "all N ads" count — the server excludes them from the launch too.
  const selectableAdIds = launchableAdIds(previewTickets, namingOverrides);
  const readyAdCount = selectableAdIds.length;
  const selectedAdCount = selectedAdIds.size;

  // Draft no longer auto-seeds the selection — it CARRIES whatever is selected. An
  // empty Draft selection is safe because the submit is disabled at 0 (KTD-10: the
  // backend's empty⇒all is never reached for a draft). Use the "select all" checkbox
  // to draft every ad; uncheck to draft a portion.

  function toggleAllAds(checked: boolean) {
    setSelectedAdIds(checked ? new Set(selectableAdIds) : new Set());
  }

  function toggleNamingOverride(ticketId: string, checked: boolean) {
    const next = toggleOverride(previewTickets, namingOverrides, selectedAdIds, ticketId, checked);
    setNamingOverrides(next.overrides);
    setSelectedAdIds(next.selected);
  }

  // True when the WS status indicates the preview run reached a terminal
  // failure (FAILED / ABORTED) — shows a clear label on the disabled primary
  // button instead of "Starting…". The backend emits `aborted`, never
  // `cancelled`; `drafted` is a success and only occurs AFTER launch.
  const previewTerminalFailure =
    atPreview &&
    effectiveRunStatus !== null &&
    ["failed", "aborted"].includes(effectiveRunStatus.status.toLowerCase());

  // The primary CTA is mode- and selection-aware (U8/U9). Draft now mirrors the
  // Launch labels with the ad count (it honors the selection — U7); 0 selected is a
  // deliberate "draft nothing" the submit blocks (KTD-10), so its label must NOT
  // inherit Published's 0⇒"all". Published with no selection launches ALL (empty⇒all
  // blast radius made explicit); a subset is exactly K of N ADS (not tickets).
  const adWord = (n: number) => `ad${n !== 1 ? "s" : ""}`;
  const ctaLabel =
    launchMode === "DRAFT"
      ? selectedAdCount === 0
        ? "Select at least one ad to draft"
        : selectedAdCount === readyAdCount
          ? `Draft all ${readyAdCount} ${adWord(readyAdCount)}`
          : `Draft ${selectedAdCount} of ${readyAdCount} ${adWord(readyAdCount)}`
      : selectedAdCount === 0
        ? `Launch all ${readyAdCount} ${adWord(readyAdCount)}`
        : `Launch ${selectedAdCount} of ${readyAdCount} ${adWord(readyAdCount)}`;

  // The Settings summary block (read-only) that sits atop the Preview content.
  const settingsBlock = (readOnly: boolean, showErr: boolean) => (
    <Step3LaunchDefaults
      selectedCount={selectedCount}
      values={defaults}
      onChange={setDefaults}
      showErrors={showErr}
      productKey={productKey}
      disabled={readOnly}
    />
  );

  const previewBlock = (
    <Step4Preview
      previewRunId={previewRunId}
      runStatus={effectiveRunStatus}
      connectionStatus={runStatus.connection}
      previewData={previewQuery.data ?? null}
      isPreviewLoading={previewQuery.isLoading}
      previewError={previewQuery.isError ? previewQuery.error : null}
      onRetryPreview={() => void previewQuery.refetch()}
      createError={previewCreateError}
      onRetryCreate={() => {
        // Reset create state so a fresh attempt fires.
        setPreviewCreateError(null);
        setIsCreatingRun(false);
        createPreviewRun();
      }}
      onBackToSettings={() => {
        cancelPreviewRun();
        setStep(3);
      }}
      selectedAdIds={selectedAdIds}
      onToggleAd={toggleAd}
      onToggleAllAds={toggleAllAds}
      draftMode={launchMode === "DRAFT"}
      namingOverrides={namingOverrides}
      onToggleNamingOverride={toggleNamingOverride}
    />
  );

  // ---- Render -------------------------------------------------------------

  return (
    // data-fullbleed: opt this page out of the Shell's centered max-width content
    // column so the wizard fills the full width of the main content area.
    <Box data-fullbleed w="100%">
      {/* Navigation blocker: active from step 2 onward, only in data-router context.
          WizardNavBlocker must only render when hasDataRouter is true — useBlocker
          throws an invariant inside a plain MemoryRouter. */}
      {hasDataRouter && step >= 2 && (
        <WizardNavBlocker
          atPreviewStep={step >= 4}
          onCancelPreview={cancelPreviewRun}
          bypassRef={navigatedAfterLaunch}
        />
      )}

      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Title order={2} id="launch-wizard-title" fz="lg">
          Launch a run
        </Title>
        <Button
          variant="default"
          size="xs"
          aria-label="Exit wizard"
          disabled={pendingAnyMutation}
          onClick={exit}
        >
          Exit wizard
        </Button>
      </Group>

      <Stepper
        active={step - 1}
        allowNextStepsSelect={false}
        aria-label="Launch steps"
        size="sm"
        mb="lg"
      >
        <Stepper.Step label={STEP_LABELS[1]}>
          <Step1SelectProduct
            productKey={productKey}
            flow={flow}
            format={format}
            onProductChange={changeProduct}
            onFlowChange={changeFlow}
            onFormatChange={changeFormat}
            // Enter in any step-1 field advances to step 2 (only here — the Stepper
            // mounts this content solely on step 1). next() no-ops unless a product
            // is selected, mirroring the disabled "Next" button.
            onEnterAdvance={next}
          />
        </Stepper.Step>

        <Stepper.Step label={STEP_LABELS[2]}>
          <Step2SelectTickets
            isLoading={ticketsQuery.isLoading}
            isError={ticketsQuery.isError}
            error={ticketsQuery.error}
            data={ticketsQuery.data}
            flow={flow}
            format={format}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onRetry={() => void ticketsQuery.refetch()}
            refetch={() => void ticketsQuery.refetch()}
            isRefetchingAll={ticketsQuery.isRefetching}
            onRefreshSelected={handleRefreshSelected}
            isRefreshingSelected={refreshSelected.isPending}
            refreshingIds={
              refreshSelected.isPending
                ? new Set(refreshSelected.variables ?? [])
                : undefined
            }
            copyTabPicks={copyTabPicks}
            onPickCopyTab={(pageId, tabId) =>
              setCopyTabPicks((prev) => {
                const next = { ...prev };
                if (tabId) next[pageId] = tabId;
                else delete next[pageId];
                return next;
              })
            }
          />
        </Stepper.Step>

        <Stepper.Step label={STEP_LABELS[3]}>
          {launchError && (
            <Alert color="red" variant="light" role="alert" mb="md">
              {launchError}
            </Alert>
          )}
          {multiGroup ? (
            <>
              <Text fz="sm" c="dimmed" mb="sm">
                Your selection spans {groups.length} POV groups — each group
                launches as its own run with its own page and settings.
              </Text>
              {groups.map((g) => (
                <Fieldset key={g.key} legend={g.label} mb="md">
                  <Step3LaunchDefaults
                    selectedCount={g.pageIds.length}
                    values={defaultsFor(g.key)}
                    onChange={(d) =>
                      setGroupDefaults((prev) => ({ ...prev, [g.key]: d }))
                    }
                    showErrors={step3Submitted}
                    productKey={productKey}
                    disabled={false}
                    // 2026-07-31: combine is single-run only — per-group
                    // combined ad sets would race on one name.
                    hideCombine
                  />
                </Fieldset>
              ))}
            </>
          ) : (
            settingsBlock(false, step3Submitted)
          )}
        </Stepper.Step>

        <Stepper.Step label={STEP_LABELS[4]}>
          {launchError && (
            <Alert color="red" variant="light" role="alert" mb="md">
              {launchError}
            </Alert>
          )}
          {multiGroup ? (
            /* POV fan-out: one self-contained preview section per group, each
               owning its run's WS/poll/preview/confirm. The group's Settings
               are implicit in its run; the section legend names the group. */
            groups.map((g) => (
              <GroupPreviewSection
                key={g.key}
                groupKey={g.key}
                groupLabel={g.label}
                active={step === 4}
                runId={groupRuns[g.key]?.runId ?? null}
                createError={groupRuns[g.key]?.error ?? null}
                onRetryCreate={() => {
                  setGroupRuns((prev) => ({
                    ...prev,
                    [g.key]: { runId: null, error: null },
                  }));
                  void createGroupRun(g);
                }}
                onBackToSettings={() => {
                  cancelPreviewRun();
                  setStep(3);
                }}
                launchMode={launchMode}
                onLaunched={handleGroupLaunched}
                summary={
                  productKey ? (
                    <GroupTargetSummary productKey={productKey} values={defaultsFor(g.key)} />
                  ) : null
                }
              />
            ))
          ) : (
            <>
              {/* The Settings form is editable on its own step (3). On Preview (4)
                  it stays at the top as a read-only summary (disabled) — the run is
                  already created from these values. */}
              {settingsBlock(true, false)}
              {previewBlock}
            </>
          )}
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between" align="center" mt="md" pt="md" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
        <div>
          {step > 1 && (
            <Button variant="default" onClick={back} disabled={pendingAnyMutation}>
              Back
            </Button>
          )}
        </div>
        <Group gap="sm">
          <Button variant="default" onClick={exit} disabled={pendingAnyMutation}>
            Cancel
          </Button>
          {step < 4 ? (
            <Button
              onClick={next}
              disabled={
                (step === 1 && !canAdvanceFromStep1) ||
                (step === 2 && !canAdvanceFromStep2)
              }
            >
              Next
            </Button>
          ) : multiGroup ? (
            <>
              {/* POV fan-out: the launch buttons live inside each group's
                  section (one confirm per run). The footer keeps the global
                  mode switch and shows aggregate progress. */}
              <SegmentedControl
                value={launchMode}
                onChange={(v) => setLaunchMode(v as "PUBLISHED" | "DRAFT")}
                data={launchModeOptions}
                size="sm"
                disabled={pendingAnyMutation}
                aria-label="Launch mode"
              />
              <Text fz="sm" c="dimmed" role="status">
                {launchedGroups.size} of {groups.length} groups launched — use
                each group&apos;s Launch button above.
              </Text>
            </>
          ) : (
            <>
              {/* Launch mode beside the primary action (U9). Published pushes to
                  Meta; Draft stages in AdManage only. */}
              <SegmentedControl
                value={launchMode}
                // The per-ad selection simply CARRIES across modes — switching never
                // overwrites or auto-selects. In Draft an empty selection blocks submit
                // ("Select at least one ad to draft"); in Published empty ⇒ launch all.
                onChange={(v) => setLaunchMode(v as "PUBLISHED" | "DRAFT")}
                data={launchModeOptions}
                size="sm"
                disabled={pendingAnyMutation}
                aria-label="Launch mode"
              />
              <Button
                onClick={launch}
                loading={confirmRun.isPending}
                disabled={
                  pendingAnyMutation ||
                  !previewRunId ||
                  !previewReady ||
                  allTicketsFailed ||
                  // 2026-10-06: every ready ad is held for blank naming cells and
                  // nothing is overridden — there is nothing to launch.
                  (previewTickets.length > 0 && readyAdCount === 0) ||
                  // U6 (FIX C): a terminally-failed preview run cannot be confirmed —
                  // a second click would 409 on the AWAITING_CONFIRM guard. Disable
                  // it explicitly and surface the "Start over" recovery affordance.
                  previewTerminalFailure ||
                  // U8/KTD-10: never submit an empty Draft (the backend's empty⇒all
                  // would stamp every Notion ticket). 0 selected ⇒ submit disabled.
                  (launchMode === "DRAFT" && selectedAdCount === 0)
                }
              >
                {previewTerminalFailure
                  ? "Preview failed"
                  : !previewReady && previewCreateError === null
                    ? "Starting…"
                    : ctaLabel}
              </Button>
            </>
          )}
        </Group>
      </Group>
    </Box>
  );
}
